// Async equivalent of `drizzle-orm/expo-sqlite/driver.ts`, modeled on
// `drizzle-orm/op-sqlite/driver.ts`.
//
// `drizzle-orm/expo-sqlite` is hard-wired to expo-sqlite's *synchronous* API
// (`prepareSync` / `executeSync`). That path is unusable on web — wa-sqlite's
// worker can't service the main-thread spin wait. Instead of routing through
// `drizzle-orm/sqlite-proxy` we provide a first-class async driver: a real
// `BaseSQLiteDatabase<'async', …>` backed by `ExpoSQLiteAsyncSession`, which in
// turn talks to expo-sqlite's `runAsync` / `prepareAsync` / `executeAsync` /
// `getAllAsync` / `getFirstAsync`.
//
// Reference: https://raw.githubusercontent.com/drizzle-team/drizzle-orm/48e5406027103a9fca6eb66417187c4a8b5c6aa3/drizzle-orm/src/op-sqlite/driver.ts

import onetime from "@-/com.npmjs.onetime";
import {
  DefaultLogger,
  entityKind,
  type AnyRelations,
  type DrizzleConfig,
  type EmptyRelations,
  type Logger,
} from "@-/com.npmjs.drizzle-orm";
import { isAnyObject } from "@-/com.npmjs.is-what";
import * as V1 from "@-/com.npmjs.drizzle-orm/_relations";
import * as drizzleRuntime from "drizzle-orm";
// rc.4 renames: `BaseSQLiteDatabase` → `SQLiteAsyncDatabase`, and the sync/async dialect pair
// collapsed into one `SQLiteDialect`. NOTE `SQLiteDialect` is a VALUE import here (it is
// constructed below), which is why the stale name survived type erasure and crashed the dev server
// at module evaluation with "does not provide an export named 'SQLiteAsyncDialect'".
import { SQLiteAsyncDatabase, SQLiteDialect } from "@-/com.npmjs.drizzle-orm/sqlite-core";
import { NoopCache } from "drizzle-orm/cache/core";
import { expoSQLiteCodecs } from "drizzle-orm/expo-sqlite/codecs";
import {
  toDrizzleAsyncSQLiteClient,
  type DrizzleAsyncSQLiteClient,
} from "#expo-sqlite-async/client";
import { ExpoSQLiteAsyncSession, type ExpoSQLiteAsyncRunResult } from "#expo-sqlite-async/session";

function isRelationsConfig<TRelations extends AnyRelations>(value: unknown): value is TRelations {
  return isAnyObject(value);
}

function isExtractedSchemaTables<TSchema extends Record<string, unknown>>(
  value: unknown,
): value is V1.ExtractTablesWithRelations<TSchema> {
  return isAnyObject(value);
}

function getDrizzleRuntimeFunction<T extends (...args: never[]) => unknown>(
  exportName: string,
  validate: (value: unknown) => value is T,
): T {
  const value = Reflect.get(drizzleRuntime, exportName);
  if (!validate(value)) {
    throw new TypeError(`drizzle-orm is missing runtime export: ${exportName}`);
  }
  return value;
}

const getJitCompatCheck = onetime(() =>
  getDrizzleRuntimeFunction(
    "jitCompatCheck",
    (value): value is (isEnabled: boolean | undefined) => boolean => typeof value === "function",
  ),
);

export class ExpoSQLiteAsyncDatabase<
  // Kept although rc.4's base no longer takes it: `TSchema` is this wrapper's PUBLIC generic (every
  // `drizzle<Schema, Relations>()` call site supplies it) and is used throughout this file. The
  // linter only sees that it is not forwarded to the base class.
  // oxlint-disable-next-line eslint/no-unused-vars
  TSchema extends Record<string, unknown> = Record<string, never>,
  TRelations extends AnyRelations = EmptyRelations,
  // rc.4: SQLiteAsyncDatabase<TResultKind, TRunResult, TRelations> — the `TSchema` generic was
  // removed, so it must NOT be forwarded. `TSchema` is kept on THIS class because the wrapper's own
  // public surface (and every `drizzle(...)` call site in the app) is still keyed by it.
> extends SQLiteAsyncDatabase<"async", ExpoSQLiteAsyncRunResult, TRelations> {
  static override readonly [entityKind]: string = "ExpoSQLiteAsyncDatabase";

  declare $client: DrizzleAsyncSQLiteClient;
}

/**
 * rc.5's `DrizzleConfig` takes only the relations and dropped the V1 `schema` key. This wrapper
 * still accepts `schema`, which keys its public `TSchema` generic.
 */
export type ExpoSQLiteAsyncDrizzleConfig<
  TSchema extends Record<string, unknown> = Record<string, never>,
  TRelations extends AnyRelations = EmptyRelations,
> = DrizzleConfig<TRelations> & { schema?: TSchema | undefined };

export function drizzle<
  TSchema extends Record<string, unknown> = Record<string, never>,
  TRelations extends AnyRelations = EmptyRelations,
>(
  client: unknown,
  config: ExpoSQLiteAsyncDrizzleConfig<TSchema, TRelations> = {},
): ExpoSQLiteAsyncDatabase<TSchema, TRelations> & { $client: DrizzleAsyncSQLiteClient } {
  const sqliteClient = toDrizzleAsyncSQLiteClient(client);
  // rc.5 moved value normalization (integer booleans, `text` JSON, timestamps, blobs and their
  // in-JSON casts) from the column classes into the dialect's codecs. expo-sqlite's async API
  // returns the same values as the sync one, so use drizzle's expo-sqlite codecs.
  const dialect = new SQLiteDialect({ codecs: expoSQLiteCodecs });
  let logger: Logger | undefined;
  if (config.logger === true) {
    logger = new DefaultLogger();
  } else if (config.logger !== false) {
    logger = config.logger;
  }

  let schema: V1.RelationalSchemaConfig<V1.ExtractTablesWithRelations<TSchema>> | undefined;
  if (config.schema) {
    const tablesConfig = V1.extractTablesRelationalConfig(
      config.schema,
      V1.createTableRelationsHelpers,
    );
    if (!isExtractedSchemaTables<TSchema>(tablesConfig.tables)) {
      throw new TypeError("Invalid relational schema tables config");
    }
    schema = {
      fullSchema: config.schema,
      schema: tablesConfig.tables,
      tableNamesMap: tablesConfig.tableNamesMap,
    };
  }

  const relations = config.relations ?? {};
  if (!isRelationsConfig<TRelations>(relations)) {
    throw new TypeError("Invalid drizzle relations config");
  }
  const session = new ExpoSQLiteAsyncSession<
    TSchema,
    TRelations,
    V1.ExtractTablesWithRelations<TSchema>
  >(sqliteClient, dialect, relations, schema, {
    cache: config.cache,
    logger,
    paramsInErrors: config.paramsInErrors,
    useJitMappers: getJitCompatCheck()(config.jit),
  });
  const db = new ExpoSQLiteAsyncDatabase<TSchema, TRelations>(
    "async",
    dialect,
    session,
    // rc.4 removed the `schema` parameter from SQLiteAsyncDatabase's ctor — the next slot is
    // `forbidJsonb: boolean`, so passing the schema config here was a type error, not an extra arg.
    relations,
  );
  db.$client = sqliteClient;
  const cache = config.cache ?? new NoopCache();
  db.$cache = {
    invalidate: cache.onMutate.bind(cache),
  };
  return db;
}
